/**
 * Аудиосистема с мягкими приятными звуками CaseMania (WAV PCM 44.1kHz).
 * Звук прокрутки: деликатный тактильный щелчок (без резкости и гула).
 * Звук выпадения: чистый двухтональный звон в стиле Apple Pay (C6 -> G6) без тяжелых басов.
 * Использует Web Audio API BufferSource для ультра-быстрого воспроизведения с нулевой задержкой.
 */

let audioCtx = null
let spinBuffer = null
let winBuffer = null
let clickBuffer = null
let loadingPromise = null

function getAudioContext() {
  if (typeof window === 'undefined') return null
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (AudioContextClass) {
      audioCtx = new AudioContextClass()
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {})
  }
  return audioCtx
}

// Предзагрузка и декодирование аудио-буферов
export async function preloadSounds() {
  const ctx = getAudioContext()
  if (!ctx) return
  if (spinBuffer && winBuffer) return
  if (loadingPromise) return loadingPromise

  loadingPromise = (async () => {
    try {
      const [spinRes, winRes, clickRes] = await Promise.all([
        fetch('/sounds/spin.wav'),
        fetch('/sounds/win.wav'),
        fetch('/sounds/click.wav').catch(() => null),
      ])
      const decodes = [
        spinRes.ok ? spinRes.arrayBuffer().then((buf) => ctx.decodeAudioData(buf)) : null,
        winRes.ok ? winRes.arrayBuffer().then((buf) => ctx.decodeAudioData(buf)) : null,
        clickRes && clickRes.ok ? clickRes.arrayBuffer().then((buf) => ctx.decodeAudioData(buf)) : null,
      ]
      const [spinBuf, winBuf, clkBuf] = await Promise.all(decodes)
      if (spinBuf) spinBuffer = spinBuf
      if (winBuf) winBuffer = winBuf
      if (clkBuf) clickBuffer = clkBuf
    } catch (e) {
      console.warn('Could not load sound files, falling back to synthesizer', e)
    }
  })()

  return loadingPromise
}

export function playTickSound(volume = 0.22) {
  const ctx = getAudioContext()
  if (!ctx) return

  if (spinBuffer) {
    try {
      const source = ctx.createBufferSource()
      source.buffer = spinBuffer
      const gain = ctx.createGain()
      gain.gain.value = volume
      source.connect(gain)
      gain.connect(ctx.destination)
      source.start()
      return
    } catch {}
  }

  // Fallback синтезатор: тихий мягкий щелчок без низких частот
  try {
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(1350, now)
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.015)
    gain.gain.setValueAtTime(volume * 0.4, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.015)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.016)
  } catch {}
}

export function playWinSound(volume = 0.38) {
  const ctx = getAudioContext()
  if (!ctx) return

  if (winBuffer) {
    try {
      const source = ctx.createBufferSource()
      source.buffer = winBuffer
      const gain = ctx.createGain()
      gain.gain.value = volume
      source.connect(gain)
      gain.connect(ctx.destination)
      source.start()
      return
    } catch {}
  }

  // Fallback синтезатор: чистый кристальный перезвон Apple Pay (C6 -> G6)
  try {
    const now = ctx.currentTime
    // Тон 1: C6 (1046.5 Hz)
    const osc1 = ctx.createOscillator()
    const g1 = ctx.createGain()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(1046.5, now)
    g1.gain.setValueAtTime(0.001, now)
    g1.gain.linearRampToValueAtTime(volume * 0.35, now + 0.005)
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.28)
    osc1.connect(g1)
    g1.connect(ctx.destination)
    osc1.start(now)
    osc1.stop(now + 0.29)

    // Тон 2: G6 (1567.98 Hz)
    const t2 = now + 0.118
    const osc2 = ctx.createOscillator()
    const g2 = ctx.createGain()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(1567.98, t2)
    g2.gain.setValueAtTime(0.001, t2)
    g2.gain.linearRampToValueAtTime(volume * 0.45, t2 + 0.005)
    g2.gain.exponentialRampToValueAtTime(0.001, t2 + 0.95)
    osc2.connect(g2)
    g2.connect(ctx.destination)
    osc2.start(t2)
    osc2.stop(t2 + 1.0)
  } catch {}
}

export function playClickSound(volume = 0.2) {
  const ctx = getAudioContext()
  if (!ctx) return

  if (clickBuffer) {
    try {
      const source = ctx.createBufferSource()
      source.buffer = clickBuffer
      const gain = ctx.createGain()
      gain.gain.value = volume
      source.connect(gain)
      gain.connect(ctx.destination)
      source.start()
      return
    } catch {}
  }
}
